import {
  useApiClient,
  WebshopReviewQueries,
  webshopReviewGetReviewsService,
} from '@cldsystems/ws-api-client';
import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';
import useMyProductReview, { buildReviewQueryKey } from '@/hooks/use-product-reviews';
import { useAppContext } from '@/providers/app-provider/app-provider';
import {
  buildReviewsForRating,
  calculateProductReviewRating,
  REVIEW_RATING_FETCH_SIZE,
  type ReviewWithRanking,
} from '@/utils/product-review-rating';

/**
 * Average and count over every locale's reviews for the product.
 *
 * `productIds` are the sibling ids for one SKU. Rating a single id scores the
 * Romanian and Hungarian listings of the same item separately.
 */
const useProductAverageRating = (productIds: string[]) => {
  const { isStudioPreview } = useAppContext<{ isStudioPreview?: boolean }>();
  const { httpClient, hasSchema } = useApiClient();
  const { myReview, userId } = useMyProductReview(productIds);

  const { reviews, updatedAt } = useQueries({
    queries: productIds.map(productId => {
      const queries = { productId, from: '0', size: REVIEW_RATING_FETCH_SIZE };

      return {
        queryKey: buildReviewQueryKey(WebshopReviewQueries.GetReviews, queries),
        queryFn: webshopReviewGetReviewsService({
          queries,
          httpClient,
          hasSchema: Boolean(hasSchema),
        }),
        enabled: !isStudioPreview && Boolean(httpClient),
      };
    }),
    combine: results => ({
      reviews: results.flatMap(result => result.data ?? []),
      // `combine` rebuilds its result on every render, so the merged array is never
      // referentially stable — this is the cheap stand-in for "the data changed".
      updatedAt: results.reduce((latest, result) => Math.max(latest, result.dataUpdatedAt), 0),
    }),
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: `updatedAt` tracks query refresh; `reviews`/`myReview` drive rating
  return useMemo(() => {
    if (isStudioPreview) return { average: 0, count: 0 };

    const reviewsForRating = buildReviewsForRating<ReviewWithRanking>(reviews, myReview, userId);

    return calculateProductReviewRating(reviewsForRating);
  }, [isStudioPreview, userId, reviews, myReview, updatedAt]);
};

export default useProductAverageRating;
